import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { TOKEN_KEY } from './AuthContext';

// Seed files shipped with the build. Used only until something is published
// to the backend store (Netlify Blobs) via the admin dashboard.
const SEED_ENDPOINTS = {
  blogs: '/data/blogs.json',
  videos: '/data/videos.json',
  webinars: '/data/webinars.json',
};

const ContentContext = createContext(null);

// Load a kind from the backend; fall back to the seed file if nothing has been
// published yet, then to an empty list if even that is unavailable.
const loadInitial = async (kind) => {
  try {
    const res = await fetch(`/api/content?kind=${kind}`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.items)) return data.items;
    }
  } catch {
    // backend unreachable (e.g. plain `npm start` without netlify dev) — fall through
  }
  try {
    const res = await fetch(SEED_ENDPOINTS[kind]);
    if (res.ok) return await res.json();
  } catch {
    // ignore
  }
  return [];
};

// Publish a full list to the backend. Requires the write token from login.
const publish = async (kind, list) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return { ok: false, error: 'Not logged in — your session may have expired. Log in again.' };
  }
  try {
    const res = await fetch('/api/content', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind, items: list }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) return { ok: true };
    return { ok: false, error: data.error || `Publish failed (HTTP ${res.status})` };
  } catch {
    return { ok: false, error: 'Could not reach the publishing service. Check your connection.' };
  }
};

export const ContentProvider = ({ children }) => {
  const [blogs, setBlogs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([loadInitial('blogs'), loadInitial('videos'), loadInitial('webinars')]).then(
      ([b, v, w]) => {
        if (!alive) return;
        setBlogs(b);
        setVideos(v);
        setWebinars(w);
        setReady(true);
      }
    );
    return () => {
      alive = false;
    };
  }, []);

  const setLocal = useCallback((kind, list) => {
    if (kind === 'blogs') setBlogs(list);
    if (kind === 'videos') setVideos(list);
    if (kind === 'webinars') setWebinars(list);
  }, []);

  // Optimistically update the UI, then publish to the backend so the change is
  // live for every visitor. Returns the publish result so callers can report it.
  const update = useCallback(
    async (kind, list) => {
      setLocal(kind, list);
      return publish(kind, list);
    },
    [setLocal]
  );

  const upsert = useCallback(
    (kind, item) => {
      const list = kind === 'blogs' ? blogs : kind === 'videos' ? videos : webinars;
      const idx = list.findIndex((x) => x.id === item.id);
      const next = idx >= 0 ? list.map((x, i) => (i === idx ? item : x)) : [item, ...list];
      return update(kind, next);
    },
    [blogs, videos, webinars, update]
  );

  const remove = useCallback(
    (kind, id) => {
      const list = kind === 'blogs' ? blogs : kind === 'videos' ? videos : webinars;
      return update(kind, list.filter((x) => x.id !== id));
    },
    [blogs, videos, webinars, update]
  );

  const exportJson = useCallback(
    (kind) => {
      const list = kind === 'blogs' ? blogs : kind === 'videos' ? videos : webinars;
      const blob = new Blob([JSON.stringify(list, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [blogs, videos, webinars]
  );

  // Reset publishes the shipped seed list back to the backend for everyone.
  const resetToDefaults = useCallback(
    async (kind) => {
      let fresh = [];
      try {
        const res = await fetch(SEED_ENDPOINTS[kind]);
        if (res.ok) fresh = await res.json();
      } catch {
        // ignore
      }
      setLocal(kind, fresh);
      return publish(kind, fresh);
    },
    [setLocal]
  );

  return (
    <ContentContext.Provider
      value={{ blogs, videos, webinars, ready, upsert, remove, exportJson, resetToDefaults }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
};
